import {
	ArrowLeft,
	ArrowsLeftRight,
	Question,
	CheckCircle,
	Headset,
	WarningCircle,
	ChatCircleDots,
} from '@phosphor-icons/react';
import PrimaryButton from '../../../../components/atoms/Button';
import { LinkingSecurityPoints } from '../LinkingFlowStep1';
import SATCredentialsForm from '../SATCredentialsForm';
import {
	resolveBindError,
	offersCredentialHelp,
	type BindErrorCode,
} from '../bindErrors';
import AsatPromoDrawer from './AsatPromoDrawer';
import { useOpenSupportChat } from '@/shared/support/supportChat';
import LinkingHelpVideo from '../LinkingHelpVideo';
import { useSupportRescueThreshold } from '../experiments/useSupportRescueThreshold';
import {
	markRescueDismissed,
	readFailedAttempts,
	readRescueDismissedAt,
	SUPPORT_RESCUE_MESSAGE,
} from '../failedAttempts';
import SecurityTag from '../SecurityTag';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import ModalFrame from '../../../../components/molecules/ModalFrame';
import Modal from '../../../../components/molecules/Modal/Modal';
import { useNavigate } from 'react-router-dom';
import ROUTES from '../../../../shared/constants/routes';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { getStorage } from '../../../../shared/utils/general.util';
import InputFileNew from '../../../../components/atoms/InputFileNew';
import STORAGE_KEYS from '../../../../shared/constants/storage';
import useEvents from '../../../../hooks/useEvents';
import { EVENT_TYPES } from '../../../../shared/constants/events';
import { trackGTMEvent } from '../../../../shared/analytics/gtm';
import { FRONTAPP_ARTICLES } from '../../../../shared/constants/articles';
import ModalFailed from './ModalFailed';
import { CLARITY_EVENTS } from '../../../../shared/constants/clarityEvents';
import FrequentAnswers from '@/components/FrequentAnswers';
import Input from '../../../../components/atoms/Input';
import { certKindRule } from '@/features/sat-credentials';
import { useCredentialsForm } from '../orchestration/useCredentialsForm';
import {
	useLinkingTracking,
	type HelpModalCloseReason,
	type HelpModalOption,
	type HelpModalTrigger,
	type RfcHelpEntryPoint,
} from '../orchestration/useLinkingTracking';

const Link = ({
	children,
	href,
	onClick,
}: {
	children: React.ReactNode;
	href?: string;
	onClick?: () => void;
}) => {
	if (onClick) {
		return (
			<button
				onClick={onClick}
				className='italic font-medium text-h-primary cursor-pointer'
			>
				{children}
			</button>
		);
	}

	return (
		<a
			href={href}
			target='_blank'
			rel='noopener noreferrer'
			className='italic font-medium text-h-primary cursor-pointer'
		>
			{children}
		</a>
	);
};

const LinkingFlowStep2 = ({
	linkingOptionSelected,
	setLinkingOptionSelected,
	setStep,
	onSubmit,
	showModalFailed,
	isLoading,
	rewardBanner,
	directEntry = false,
}: {
	linkingOptionSelected: 'link' | 'efirma';
	setLinkingOptionSelected: (option: 'link' | 'efirma') => void;
	setStep: (step: number) => void;
	onSubmit: (data: any) => void;
	showModalFailed: {
		errorMessage: string;
	};
	setShowModalFailed: (showModalFailed: { errorMessage: string }) => void;
	isLoading: boolean;
	/** La promesa del experimento. Primer hijo de la tarjeta para que la franja
	 * quede a sangre; ya viene con su bleed desde LinkingFlow. */
	rewardBanner?: ReactNode;
	/** El usuario aterrizó aquí sin pasar por el selector (brazo de tratamiento
	 * del experimento de entrada). Enciende la card del video, que en el control
	 * vive en la portada del paso 1: es lo que mantiene el ACCESO AL VIDEO igual
	 * en los dos brazos, para que el experimento mida sólo dónde aterrizas y no
	 * además si el video estaba o no. */
	directEntry?: boolean;
}) => {
	const navigate = useNavigate();
	const openSupportChat = useOpenSupportChat();
	const { trackEventClarity } = useEvents();
	const { trackAction, trackMethodSelected, trackCredentialsView } =
		useLinkingTracking();
	const [selectedSite, setSelectedSite] = useState<string | null>(null);
	const [showAsatPromo, setShowAsatPromo] = useState(false);
	// Se abre solo al alcanzar el umbral, no con un botón: quien lleva N
	// tropiezos ya demostró que no lo saca corrigiendo el formulario, así que la
	// ayuda sale a buscarlo. `rescueAutoOpened` la deja cerrarse y no reaparecer
	// en el mismo montaje; cada fallo nuevo remonta el paso y la vuelve a ofrecer.
	const [showSupportRescue, setShowSupportRescue] = useState(false);
	const rescueAutoOpened = useRef(false);
	// `openedFrom` recuerda qué puerta abrió el modal para que las opciones y el
	// cierre lo lleven en su payload: sin eso el embudo de ayuda no se puede
	// partir por puerta («?» del subtítulo vs link de recuperación vs error).
	const [showModal, setShowModal] = useState<{
		passwordRecovery: boolean;
		openedFrom: HelpModalTrigger | null;
	}>({ passwordRecovery: false, openedFrom: null });
	// El drawer «Generar mi RFC» vive dentro de SATCredentialsForm, pero también
	// lo abre el «?» azul del subtítulo (en RFC y contraseña); por eso el estado
	// sube aquí.
	const [rfcHelp, setRfcHelp] = useState<{
		open: boolean;
		entryPoint: RfcHelpEntryPoint;
	}>({ open: false, entryPoint: 'field_help' });

	const {
		control,
		handleSubmit,
		trigger,
		errors,
		rfcPrefill,
		initialDrawerCurp,
		initialDrawerFullName,
		handleRfcGenerated,
	} = useCredentialsForm({
		onRfcPrefilled: (source) => trackAction('link', 'rfc_prefilled', { source }),
		onRfcPrefillMissed: (missed) =>
			trackAction('link', 'rfc_prefill_missed', missed),
	});

	const pageContent = {
		link: {
			title: 'Conecta tu cuenta del SAT',
			description:
				'Utiliza el mismo RFC y la contraseña que usas para ingresar al portal del SAT',
			tooltip:
				'La contraseña del SAT es una clave personal que debe tener al menos 8 caracteres y un número.',
			faqs: [
				{
					question: '¿Cuál es mi contraseña del SAT y cómo puedo recuperarla?',
					answer: (
						<>
							<p>
								La contraseña del SAT{' '}
								<b>debe tener al menos 8 caracteres y un número.</b> Te permite
								acceder a los servicios en línea del SAT, cómo presentar tu
								declaración anual, consultar facturas, etc.
							</p>
							<p>
								Te compartimos un{' '}
								<Link onClick={() => setSelectedSite(FRONTAPP_ARTICLES.ciec)}>
									artículo
								</Link>{' '}
								que te ayudará a conectar tu cuenta del SAT.
							</p>

							<p>
								Si aún no cuentas con tu contraseña o la has olvidado, puedes
								generarla fácilmente en línea desde el{' '}
								<Link href='https://satid.sat.gob.mx/'>
									portal oficial del SAT
								</Link>
								.
							</p>
						</>
					),
				},
			],
		},
		efirma: {
			title: 'Conecta tu cuenta del SAT',
			description:
				'Con tu e.firma utilizando los archivos .cer y .key que te proporcionaron cuando realizaste el trámite en las oficinas del SAT.',
			tooltip:
				'La contraseña de la clave privada es la que proporcionaste el día que tramitaste tu e.firma y debe tener al menos 8 caracteres y un número.',
			faqs: [
				{
					question: '¿Cúal es la e.firma y cómo puedo obtenerla?',
					answer: (
						<>
							<p>
								La e.firma es un servicio que te permite firmar documentos
								electrónicos de manera segura, también te permite consultar tus
								facturas y presentar tus declaraciones.{' '}
							</p>

							<b>Para obtenerla, debes tramitarlo en las oficinas del SAT.</b>

							<p>
								Recuerda que puedes conectar tu cuenta del SAT por{' '}
								<Link
									onClick={() => {
										setLinkingOptionSelected('link');
										handleCloseModal();
									}}
								>
									<b>RFC y contraseña</b>
								</Link>{' '}
								también.
							</p>
						</>
					),
				},
			],
		},
	};

	const storedFailedInfo = JSON.parse(
		getStorage().getItem(STORAGE_KEYS.LINKING_FAILED_INFO) || '{}',
	);
	const failedInfo = storedFailedInfo?.errorMessage;
	const failedCode = storedFailedInfo?.errorCode as BindErrorCode | undefined;
	const failedMethod = storedFailedInfo?.linkingOptionSelected;
	const rawErrorMessage = showModalFailed?.errorMessage || failedInfo;
	const errorMatchesMethod =
		!failedMethod || failedMethod === linkingOptionSelected;

	const resolvedBindError =
		isLoading || !errorMatchesMethod || (!rawErrorMessage && !failedCode)
			? null
			: resolveBindError(failedCode, rawErrorMessage);
	const submitErrorMessage = resolvedBindError?.message ?? '';
	// El error que apunta a un campo se pinta PEGADO a ese campo (sin fondo);
	// solo lo que no tiene campo dueño se queda como banner del formulario.
	const isCredentialFieldError =
		resolvedBindError?.action === 'highlight_password';
	// El usuario ya demostró que no lo va a sacar corrigiendo el formulario, así
	// que la salida deja de ser un producto y pasa a ser una persona. Cuenta CIEC
	// y e.firma juntos (ver failedAttempts). CUÁNTOS tropiezos hacen falta es el
	// brazo del experimento: 3 en control, 1 en tratamiento.
	const failedAttempts = readFailedAttempts();
	const supportRescueThreshold = useSupportRescueThreshold();
	const needsSupportRescue = failedAttempts >= supportRescueThreshold;
	// El upsell de ASAT se calla cuando el rescate está encendido: dos CTAs
	// secundarios compitiendo debajo del principal es ruido, y a quien lleva tres
	// intentos fallidos se le ofrece ayuda antes que otra compra.
	const canOfferAsat =
		!!submitErrorMessage &&
		offersCredentialHelp(failedCode) &&
		!needsSupportRescue;
	const asatRequirement = submitErrorMessage
		? `No pude conectar mi cuenta del SAT. Error: ${submitErrorMessage} Método de vinculación: RFC y contraseña.`
		: '';
	const errorBanner =
		submitErrorMessage && !isCredentialFieldError ? (
			<div className='bg-h-error-90 text-h-error-30 flex items-start gap-2 rounded-lg px-4 py-3 font-medium'>
				<WarningCircle size={18} weight='fill' className='mt-0.5 shrink-0' />
				<span className='min-w-0'>{submitErrorMessage}</span>
			</div>
		) : null;

	// Salida al otro método, SIEMPRE visible y fuera del experimento. El único
	// cambio de método que existía vivía en el modal de error y en un enlace
	// enterrado en el FAQ: sólo después de fallar. Quien aterriza directo en un
	// formulario lo necesita, pero quien llegó por el selector también.
	//
	// Emite el MISMO `select_method` que el selector, así que el análisis sigue
	// leyendo un solo evento y se queda con el primero por usuario.
	const otherMethod = linkingOptionSelected === 'efirma' ? 'link' : 'efirma';
	const methodSwitchButton = (
		<button
			type='button'
			onClick={() => {
				trackMethodSelected(otherMethod);
				setLinkingOptionSelected(otherMethod);
			}}
			className='border-h-primary-50 text-h-primary-50 flex h-12 w-full items-center justify-center gap-1.5 rounded-2xl border text-base font-semibold'>
			<ArrowsLeftRight size={20} weight='bold' />
			{otherMethod === 'link'
				? 'Prefiero usar RFC y contraseña'
				: 'Prefiero usar mi e.firma'}
		</button>
	);

	// Va DEBAJO del CTA: la acción principal sigue siendo reintentar con las
	// credenciales corregidas; pedir ayuda es la salida de quien ya no puede.
	const credentialHelpButton = canOfferAsat ? (
		<button
			type='button'
			onClick={() => setShowAsatPromo(true)}
			className='border-h-primary-50 text-h-primary-50 flex h-12 w-full items-center justify-center gap-1.5 rounded-2xl border text-base font-semibold'>
			<Headset size={20} weight='fill' />
			¿Problemas con tus credenciales?
		</button>
	) : null;

	// El payload que comparten las tres caras del rescate (mostrado, escrito,
	// descartado): sin `failed_attempts` no se puede leer el brazo del
	// experimento en el dato, que es el punto de medirlo.
	const supportRescuePayload = {
		error_code: failedCode ?? null,
		failed_attempts: failedAttempts,
	};

	// Cierra la hoja sin decir nada: es el camino de quien SÍ escribió por
	// el chat, que ya emitió `support_rescue_opened`.
	const closeSupportRescue = () => {
		markRescueDismissed(failedAttempts);
		setShowSupportRescue(false);
	};

	// Cierra la hoja descartando la ayuda. Es la otra mitad de la conversión del
	// modal: lo vio y lo ignoró, frente a lo vio y escribió.
	const dismissSupportRescue = () => {
		trackAction(
			linkingOptionSelected,
			'support_rescue_dismissed',
			supportRescuePayload,
		);
		closeSupportRescue();
	};

	useEffect(() => {
		if (!needsSupportRescue || rescueAutoOpened.current) return;
		// Ya la cerró con este mismo número de tropiezos: no se le vuelve a poner
		// enfrente hasta que falle otra vez.
		if (failedAttempts <= readRescueDismissedAt()) return;
		rescueAutoOpened.current = true;
		setShowSupportRescue(true);
		// El DENOMINADOR del experimento: sin el mostrado, `support_rescue_opened`
		// no tiene contra qué dividirse y el umbral no se puede comparar.
		trackAction(linkingOptionSelected, 'support_rescue_shown', {
			error_code: failedCode ?? null,
			failed_attempts: failedAttempts,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [needsSupportRescue, failedAttempts]);

	const helpModalPayload = {
		opened_from: showModal.openedFrom,
		error_code: failedCode ?? null,
		had_error: Boolean(failedInfo),
	};

	const handleCloseModal = async (reason: HelpModalCloseReason = 'dismiss') => {
		if (showModal.passwordRecovery) {
			trackAction(linkingOptionSelected, 'help_modal_closed', {
				...helpModalPayload,
				close_reason: reason,
			});
		}
		if (failedInfo) {
			trackEventClarity(CLARITY_EVENTS.NEW_LINKING_CLOSE_MODAL_FAILED);
		}
		setShowModal({ passwordRecovery: false, openedFrom: null });
		getStorage().removeItem(STORAGE_KEYS.LINKING_FAILED_INFO);
	};

	// Qué se tocó dentro del modal. El cambio de método desde aquí emite además
	// el MISMO `select_method` que el selector y el botón de abajo: el análisis
	// lee un solo evento para «eligió método» y se queda con el primero.
	const handleHelpOptionClick = (option: HelpModalOption) => {
		trackAction(linkingOptionSelected, 'help_modal_option_clicked', {
			...helpModalPayload,
			option,
		});
		if (option === 'switch_method') trackMethodSelected(otherMethod);
	};

	const openHelpModal = (openedFrom: HelpModalTrigger) =>
		openHelp(() => setShowModal({ passwordRecovery: true, openedFrom }));

	// Pasado el umbral, TODA superficie de ayuda va al chat prioritario. Quien
	// lleva N tropiezos ya demostró que no lo resuelve un artículo ni cambiar de
	// método; ofrecerle otra vez la misma ayuda que ya no le sirvió es ruido.
	const openHelp = (open: () => void) => {
		if (needsSupportRescue) {
			setShowSupportRescue(true);
			return;
		}
		open();
	};

	const openPasswordRecovery = (action: 'help' | 'error') => {
		trackAction(
			linkingOptionSelected,
			action === 'help' ? 'password_help_opened' : 'password_error_clicked',
			{ error_code: failedCode ?? null },
		);
		openHelpModal(action === 'help' ? 'password_help' : 'password_error');
	};

	useEffect(() => {
		window.scrollTo({
			top: 0,
			behavior: 'smooth',
		});
	}, [linkingOptionSelected]);

	useEffect(() => {
		trackGTMEvent(EVENT_TYPES.GTM_LINKING_STEP_2_VIEWED, {
			type: linkingOptionSelected,
			has_previous_error: Boolean(failedInfo),
		});

		trackCredentialsView(linkingOptionSelected, failedInfo);
		// El fallo de vinculación no abre avisos automáticos (banner rojo +
		// superficies activas). Aquí ModalFailed queda solo como ayuda de
		// recuperación al tocar "¿Cómo recupero…?", no se auto-abre en error.
	}, []);

	return (
		<section className='px-3 py-6 max-w-[600px] mx-auto flex flex-col gap-4 fade-in-scale text-sm -mt-3 md:-mt-0'>
			<div className='bg-white rounded-xl shadow px-4 md:px-6 py-4 md:py-8 flex flex-col gap-4 md:gap-6 relative overflow-hidden '>
				{rewardBanner}

				<ModalFailed
					open={showModal.passwordRecovery && !needsSupportRescue}
					failedInfo={failedInfo}
					handleCloseModal={handleCloseModal}
					onOptionClick={handleHelpOptionClick}
					linkingOptionSelected={linkingOptionSelected}
					setLinkingOptionSelected={setLinkingOptionSelected}
					setStep={setStep}
					setSelectedSite={setSelectedSite}
				/>
				{selectedSite && (
					<ModalFrame
						open
						onClose={() => setSelectedSite(null)}
						src={selectedSite}
					/>
				)}

				<Modal
					open={showSupportRescue}
					onClose={dismissSupportRescue}
					sx='max-w-md'>
					<div className='flex flex-col items-center gap-3 py-2 text-center'>
						<div className='bg-h-primary-90 flex size-14 items-center justify-center rounded-full'>
							<Headset size={28} weight='fill' className='text-h-primary-40' />
						</div>
						<h3 className='text-h-text-heading text-lg font-semibold md:text-xl'>
							Te ayudamos a vincularte
						</h3>
						<p className='text-h-text-medium text-base leading-relaxed'>
							Mándanos un mensaje a nuestro chat de atención prioritaria y te
							acompañamos a conectar tu cuenta del SAT.
						</p>
						<div className='mt-2 flex w-full flex-col gap-2'>
							<button
								type='button'
								onClick={() => {
									trackAction(
										linkingOptionSelected,
										'support_rescue_opened',
										supportRescuePayload,
									);
									closeSupportRescue();
									openSupportChat(
										'vinculacion_sat_ayuda',
										SUPPORT_RESCUE_MESSAGE,
									);
								}}
								className='bg-h-primary-50 flex h-12 w-full items-center justify-center gap-2 rounded-2xl text-base font-semibold text-white'>
								<ChatCircleDots size={20} weight='fill' />
								Enviar mensaje
							</button>
							<button
								type='button'
								onClick={dismissSupportRescue}
								className='border-h-primary-50 text-h-primary-50 flex h-12 w-full items-center justify-center rounded-2xl border text-base font-semibold'>
								Cerrar
							</button>
						</div>
					</div>
				</Modal>

				<AsatPromoDrawer
					open={showAsatPromo}
					onOpenChange={setShowAsatPromo}
					initialRequirement={asatRequirement}
					method={linkingOptionSelected}
					entryPoint='credentials_error'
				/>

				<div className='flex items-center gap-2 justify-between'>
					<button onClick={() => setStep(1)}>
						<ArrowLeft
							size={22}
							className='text-h-primary'
							weight='bold'
						/>
					</button>
					<SecurityTag />
					<div className='w-[32px]' />
				</div>

				<div className='text-center flex flex-col gap-1'>
					<h2 className='text-lg md:text-xl font-medium'>
						{pageContent[linkingOptionSelected].title}
					</h2>
					<p className='font-light tracking-tight'>
						{pageContent[linkingOptionSelected].description}{' '}
						{/* El «?» era gris al 30% y solo hablaba al pasar el mouse: en
						    celular no existía. Ahora es azul, un poco más grande y al
						    tocarlo abre ayuda: en RFC y contraseña el drawer «Generar mi
						    RFC» (lo que atora ahí es no saber el RFC); en e.firma el modal
						    de ayuda (video, artículo, otro método). El tooltip de
						    escritorio se queda como adelanto. */}
						<TooltipProvider>
							<Tooltip
								// Solo escritorio: Radix no abre tooltips con touch. Cuenta
								// cuántos se asoman al «?» sin llegar a tocarlo.
								onOpenChange={(tooltipIsOpen) => {
									if (!tooltipIsOpen) return;
									trackAction(linkingOptionSelected, 'description_tooltip_shown', {
										error_code: failedCode ?? null,
									});
								}}>
								<TooltipTrigger asChild>
									<button
										type='button'
										aria-label={
											linkingOptionSelected === 'link'
												? 'Generar mi RFC'
												: 'Ayuda para conectar tu cuenta del SAT'
										}
										onClick={() => {
											trackAction(
												linkingOptionSelected,
												'description_help_opened',
												{ error_code: failedCode ?? null },
											);
											if (linkingOptionSelected === 'link') {
												setRfcHelp({ open: true, entryPoint: 'description_help' });
												return;
											}
											openHelpModal('description_help');
										}}
										className='text-h-primary inline-flex align-middle -mt-0.5'
									>
										<Question size={20} weight='fill' />
									</button>
								</TooltipTrigger>
								<TooltipContent>
									<p>{pageContent[linkingOptionSelected].tooltip}</p>
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					</p>
				</div>

				{linkingOptionSelected === 'link' && (
					<form
						className='flex flex-col gap-2 mt-6'
						onSubmit={handleSubmit(onSubmit)}
					>
						<SATCredentialsForm
							// Cast: SATCredentialsForm's typed surface only knows about
							// `rfc`/`password`; this form also has efirma-specific fields
							// the shared component doesn't touch.
							// eslint-disable-next-line @typescript-eslint/no-explicit-any
							control={control as any}
							rfcPrefill={rfcPrefill}
							initialCurp={initialDrawerCurp}
							initialFullName={initialDrawerFullName}
							onRfcGenerated={handleRfcGenerated}
							onPasswordRecoveryClick={() => openPasswordRecovery('help')}
							passwordError={
								isCredentialFieldError ? submitErrorMessage : undefined
							}
							onPasswordErrorClick={() => openPasswordRecovery('error')}
							rfcHelpOpen={rfcHelp.open}
							rfcHelpEntryPoint={rfcHelp.entryPoint}
							onRfcHelpOpenChange={(open, entryPoint = 'field_help') =>
								setRfcHelp({ open, entryPoint })
							}
						/>

						{errorBanner}

						<PrimaryButton
							loading={isLoading}
							label='Conectar cuenta'
							sx='mt-4'
							type='submit'
							icon={
								<CheckCircle
									weight='fill'
									size={20}
									className='opacity-50'
								/>
							}
							onClick={handleSubmit(onSubmit)}
						/>

						{methodSwitchButton}
						{credentialHelpButton}
					</form>
				)}

				{linkingOptionSelected === 'efirma' && (
					<form
						className='flex flex-col gap-2 mt-6'
						onSubmit={handleSubmit(onSubmit)}
					>
						<InputFileNew
							name='cerFile'
							control={control}
							accept='.cer'
							label='Certificado (.cer)'
							rules={{ required: true, validate: certKindRule('fiel') }}
							errorMessage={errors.cerFile?.message as string | undefined}
							onFileChange={async (file) => {
								const isValidCertificate = await trigger('cerFile');
								trackAction(
									'efirma',
									file ? 'efirma_file_attached' : 'efirma_file_removed',
									file
										? { file_kind: 'cer', is_valid: isValidCertificate }
										: { file_kind: 'cer' },
								);
							}}
						/>

						<InputFileNew
							name='keyFile'
							control={control}
							accept='.key'
							label='Clave privada (.key)'
							rules={{ required: true }}
							onFileChange={(file) =>
								trackAction(
									'efirma',
									file ? 'efirma_file_attached' : 'efirma_file_removed',
									{ file_kind: 'key' },
								)
							}
						/>

						<div className='flex flex-col gap-1 mt-1'>
							<Input
								name='efirmaPassword'
								type='password'
								label='Contraseña de la clave privada'
								placeholder='Ingresa tu contraseña de la clave privada'
								control={control}
								rules={{
									required: 'La contraseña es requerida',
									minLength: {
										value: 8,
										message: 'La contraseña debe tener al menos 8 caracteres',
									},
								}}
							/>

							{isCredentialFieldError && submitErrorMessage && (
								<button
									type='button'
									onClick={() => openPasswordRecovery('error')}
									className='text-h-error-50 mt-1.5 text-left text-sm leading-snug font-medium'>
									{submitErrorMessage}
								</button>
							)}
							<button
								className='text-h-primary font-medium mb-4 text-left'
								onClick={() => {
									trackAction('efirma', 'efirma_recovery_help_opened', {
										error_code: failedCode ?? null,
									});
									openHelpModal('efirma_recovery_link');
								}}
								type='button'
							>
								¿Cómo recupero mis archivos de la e.firma?
								<Question
									size={16}
									weight='fill'
									className='inline-block mb-0.5 '
								/>
							</button>
						</div>

						{errorBanner}

						<PrimaryButton
							loading={isLoading}
							label='Conectar e.firma'
							type='submit'
							icon={
								<CheckCircle
									weight='fill'
									size={20}
									className='opacity-50'
								/>
							}
							onClick={handleSubmit(onSubmit)}
						/>

						{methodSwitchButton}
					</form>
				)}

				<div className='flex flex-col gap-4 mt-2 flex-1 justify-end p-3 pb-4'>
					<LinkingSecurityPoints />
				</div>
			</div>

			{directEntry && <LinkingHelpVideo />}

			<FrequentAnswers
				faqs={[
					...pageContent[linkingOptionSelected].faqs,
					{
						question: '¿Es seguro conectar mi cuenta del SAT aquí?',
						answer: (
							<p>
								<b>Sí, la conexión es completamente segura.</b> Utilizamos
								cifrado de última generación para proteger tu información. Solo
								tú tienes acceso a tus datos y no realizaremos ningún movimiento
								en tu cuenta del SAT sin tu consentimiento.
							</p>
						),
					},
					{
						question: '¿Qué sucede si ingreso una contraseña incorrecta?',
						answer: (
							<>
								<p>
									En caso de ingresar una contraseña incorrecta, se mostrará un
									mensaje de error indicándolo. Puedes intentarlo nuevamente las
									veces que sea necesario, ya que no existe ningún tipo de
									bloqueo o restricción en tu cuenta por intentos fallidos.
								</p>
							</>
						),
					},
					{
						question:
							'¿Ustedes me pueden ayudar a obtener mi contraseña del SAT o de la e.firma?',
						answer: (
							<>
								<p>
									Por supuesto. Contamos con un{' '}
									<Link
										onClick={() => {
											navigate(`${ROUTES.PLANS_AND_PRICES}?flow=asat`);
											trackAction(linkingOptionSelected, 'click_asat_faq');
											trackEventClarity(
												CLARITY_EVENTS.NEW_LINKING_USER_WANT_HELP,
											);
										}}
									>
										servicio especializado
									</Link>{' '}
									para apoyarte en la obtención de tu contraseña, así como en
									cualquier otro trámite que tengas pendiente con el SAT, como
									la e.firma, agendar citas con el SAT, entre otros.
								</p>
								<p>
									Además, si contratas nuestra{' '}
									<Link
										onClick={() => {
											navigate(`${ROUTES.PLANS_AND_PRICES}?flow=subscription`);
											trackAction(
												linkingOptionSelected,
												'click_subscription_faq',
											);
											trackEventClarity(
												CLARITY_EVENTS.NEW_LINKING_USER_WANT_HELP,
											);
										}}
									>
										suscripción
									</Link>
									, te ayudamos a gestionar tu contraseña y vincular tu cuenta
									sin costo adicional. Nos encargamos de que el proceso sea
									rápido y sin complicaciones para ti.
								</p>
							</>
						),
					},
				]}
			/>
		</section>
	);
};

export default LinkingFlowStep2;
